import type { SessionStage, StudioSession } from "./studio-session";

/**
 * Les sessions que le poste garde en mémoire, et celle qui est en scène.
 *
 * Plusieurs mémorisées, une seule active : il n'y a qu'un piano-roll. Basculer,
 * c'est sortir l'une et entrer l'autre — le transport, lui, ne bouge pas, seul
 * son état est réécrit.
 *
 * L'ordre compte : la sortante gare d'abord sa position et démonte ses
 * ordonnanceurs, la nouvelle réécrit ensuite l'origine. Dans l'autre sens, la
 * sortante lirait une origine qui n'est plus la sienne et garerait une position
 * fausse.
 */
export class SessionManager {
  private readonly sessions = new Set<StudioSession>();
  private active: StudioSession | null = null;
  private readonly listeners = new Set<(active: StudioSession | null) => void>();

  constructor(private readonly stage: SessionStage) {}

  /** Met une session en mémoire, sans la faire entrer en scène. */
  remember(session: StudioSession): StudioSession {
    this.sessions.add(session);

    return session;
  }

  getActive(): StudioSession | null {
    return this.active;
  }

  getSessions(): StudioSession[] {
    return Array.from(this.sessions);
  }

  activate(session: StudioSession): void {
    if (this.active === session) {
      return;
    }

    this.sessions.add(session);
    this.active?.deactivate();
    this.active = session;
    session.activate(this.stage);
    this.notify();
  }

  /**
   * Retire une session de la mémoire et détruit sa matière.
   *
   * Si c'était l'active, il n'y a plus personne en scène : c'est à l'appelant
   * d'en activer une autre, et le faire ici choisirait à sa place.
   */
  forget(session: StudioSession): void {
    if (this.active === session) {
      this.active = null;
      this.notify();
    }

    this.sessions.delete(session);
    session.dispose();
  }

  onChange(callback: (active: StudioSession | null) => void): () => void {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  dispose(): void {
    this.active = null;

    for (const session of this.sessions) {
      session.dispose();
    }

    this.sessions.clear();
    this.listeners.clear();
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.active));
  }
}
